import { useState, useEffect } from "react";
import { useAuthStore } from "../stores/authStore";
import { getAdminBanner, type AdminBannerConfig } from "../services/adminService";

const DISMISS_KEY = "admin_banner_dismissed";

function getDismissKey(updatedAt: string): string {
  // Reset dismissal when banner content changes
  return `${DISMISS_KEY}_${updatedAt}`;
}

const bannerStyles: Record<AdminBannerConfig["type"], { container: string; icon: string; text: string }> = {
  info: {
    container: "bg-cyan-600 dark:bg-cyan-500/10 border-b border-cyan-700 dark:border-cyan-500/30",
    icon: "text-white dark:text-cyan-400",
    text: "text-white dark:text-cyan-200",
  },
  warning: {
    container: "bg-amber-600 dark:bg-amber-500/10 border-b border-amber-700 dark:border-amber-500/30",
    icon: "text-white dark:text-amber-400",
    text: "text-white dark:text-amber-200",
  },
  critical: {
    container: "bg-red-600 dark:bg-red-500/10 border-b border-red-700 dark:border-red-500/30",
    icon: "text-white dark:text-red-400",
    text: "text-white dark:text-red-200",
  },
};

const bannerIcons: Record<AdminBannerConfig["type"], string> = {
  info: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  warning: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z",
  critical: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
};

export function AdminCustomBanner() {
  const user = useAuthStore((s) => s.user);
  const [banner, setBanner] = useState<AdminBannerConfig | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;

    getAdminBanner()
      .then((config) => {
        if (config.enabled && config.message) {
          setBanner(config);
          // Check if this version was already dismissed
          const key = getDismissKey(config.updated_at);
          if (sessionStorage.getItem(key) === "1") {
            setDismissed(true);
          }
        }
      })
      .catch(() => {
        // Silently fail - banner is non-critical
      });
  }, [user?.id]);

  if (!banner || !banner.enabled || !banner.message || dismissed) return null;

  const styles = bannerStyles[banner.type];
  const iconPath = bannerIcons[banner.type];

  return (
    <div className={`${styles.container} px-4 py-2.5 flex items-center justify-between gap-3`}>
      <div className="flex items-center gap-2 min-w-0">
        <svg
          className={`w-4 h-4 ${styles.icon} flex-shrink-0`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={iconPath}
          />
        </svg>
        <span className={`text-xs sm:text-sm ${styles.text}`}>
          {banner.message}
        </span>
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          sessionStorage.setItem(getDismissKey(banner.updated_at), "1");
        }}
        className={`${styles.icon} hover:opacity-70 p-1 flex-shrink-0`}
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
